import { useCallback, useEffect, useRef } from 'react';

// How long FetchingView waits for the automation before it skips on the user's
// behalf. Lives here rather than inline in the view so the telemetry and the
// timer can never disagree about what "the wait" was (INS-1329).
export const AUTOMATION_WAIT_AUTO_SKIP_MS = 12_000;

type CaptureEvent = (
  name: string,
  properties?: Record<string, unknown>,
) => void;

/**
 * Separates the two ways a consumer stops waiting for the automation.
 *
 * `Skip Waiting For Automation` already fires for both the Skip button and the
 * auto-skip timer, which makes a deliberate "I'm done, this is fine" look
 * identical to "the app moved me on". Callers pass the reason now, and anyone
 * who leaves without either is a third case entirely — `Left Automation Wait` —
 * which is the drop-off this ticket is trying to see.
 *
 * Two exits are tracked because they mean different things:
 *  - `pagehide` — the tab was closed or navigated away from. Fires for bfcache
 *    too, and unlike `beforeunload` it is the one browsers still deliver
 *    reliably on mobile.
 *  - unmount without a resolution — the consumer moved somewhere else inside the
 *    app while the wait was still running.
 *
 * `markResolved()` is what keeps a normal skip from also counting as a
 * departure: `finish()` navigates away, so the unmount would otherwise look
 * identical to abandonment.
 */
export function useAutomationWaitTelemetry(captureEvent: CaptureEvent) {
  const enteredAtRef = useRef(Date.now());
  const resolvedRef = useRef(false);
  const exitFiredRef = useRef(false);
  // Ref so the mount-only listener always calls the current capture fn without
  // re-arming when analyticsMeta identity changes.
  const captureRef = useRef(captureEvent);
  useEffect(() => {
    captureRef.current = captureEvent;
  });

  const markResolved = useCallback(() => {
    resolvedRef.current = true;
  }, []);

  useEffect(() => {
    const fireExit = (reason: 'closedTab' | 'navigatedAway') => {
      if (resolvedRef.current || exitFiredRef.current) return;
      exitFiredRef.current = true;
      captureRef.current('Left Automation Wait', {
        reason,
        waitedMs: Date.now() - enteredAtRef.current,
      });
    };

    const onPageHide = () => fireExit('closedTab');
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      fireExit('navigatedAway');
    };
  }, []);

  const waitedMs = useCallback(() => Date.now() - enteredAtRef.current, []);

  return { markResolved, waitedMs };
}
